// Kai typography palette: a fixed type chord, three weights, and two
// line-height tiers. Sizes double every 4 steps (~1.19x chord). Weights are
// regular / medium / bold (the CircularXX faces shared across web + native).
// Line height is a unitless multiple of font size: `body` (1.4x) for UI/body
// text, `title` (1.2x) for tighter headings (the `*Title` styles).
//
// Style names read `{size}{Title?}{weight?}` with the less-common weight last:
// `small`, `smallTitle`, `smallBold`, `smallTitleBold`, `mediumMed`, ...

export const kaiFontFamily = 'CircularXX, -apple-system, BlinkMacSystemFont, "Avenir Next", "Segoe UI", Roboto, sans-serif';

const xxxSmall = 8;
const xxSmall = 10;
const xSmall = 12;
const small = 14;
const medium = 16;
const large = 20;
const xLarge = 24;
const xxLarge = 28;
const xxxLarge = 32;

export const kaiFontSize = {
  xxxSmall,
  xxSmall,
  xSmall,
  small,
  medium,
  large,
  xLarge,
  xxLarge,
  xxxLarge,
} as const;

export type KaiFontSizeName = keyof typeof kaiFontSize;

export const kaiFontWeight = {
  regular: 400,
  medium: 500,
  bold: 700,
} as const;

export type KaiFontWeight = (typeof kaiFontWeight)[keyof typeof kaiFontWeight];

export const kaiLineHeight = {
  body: 1.4,
  title: 1.2,
} as const;

export interface KaiTextStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: KaiFontWeight;
  lineHeight: number;
  fontStyle: 'normal' | 'italic';
}

// `med` aliases the medium weight so it doesn't shadow the `medium` font size.
const { regular, medium: med, bold } = kaiFontWeight;
const { body, title } = kaiLineHeight;

const build = (
  fontSize: number,
  fontWeight: KaiFontWeight,
  lineHeight: number,
): KaiTextStyle => ({
  fontFamily: kaiFontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  fontStyle: 'normal',
});

export const kaiTypography = {
  xxxSmall: build(xxxSmall, regular, body),
  xxxSmallMed: build(xxxSmall, med, body),
  xxxSmallBold: build(xxxSmall, bold, body),
  xxxSmallTitle: build(xxxSmall, regular, title),
  xxxSmallTitleMed: build(xxxSmall, med, title),
  xxxSmallTitleBold: build(xxxSmall, bold, title),

  xxSmall: build(xxSmall, regular, body),
  xxSmallMed: build(xxSmall, med, body),
  xxSmallBold: build(xxSmall, bold, body),
  xxSmallTitle: build(xxSmall, regular, title),
  xxSmallTitleMed: build(xxSmall, med, title),
  xxSmallTitleBold: build(xxSmall, bold, title),

  xSmall: build(xSmall, regular, body),
  xSmallMed: build(xSmall, med, body),
  xSmallBold: build(xSmall, bold, body),
  xSmallTitle: build(xSmall, regular, title),
  xSmallTitleMed: build(xSmall, med, title),
  xSmallTitleBold: build(xSmall, bold, title),

  small: build(small, regular, body),
  smallMed: build(small, med, body),
  smallBold: build(small, bold, body),
  smallTitle: build(small, regular, title),
  smallTitleMed: build(small, med, title),
  smallTitleBold: build(small, bold, title),

  medium: build(medium, regular, body),
  mediumMed: build(medium, med, body),
  mediumBold: build(medium, bold, body),
  mediumTitle: build(medium, regular, title),
  mediumTitleMed: build(medium, med, title),
  mediumTitleBold: build(medium, bold, title),

  large: build(large, regular, body),
  largeMed: build(large, med, body),
  largeBold: build(large, bold, body),
  largeTitle: build(large, regular, title),
  largeTitleMed: build(large, med, title),
  largeTitleBold: build(large, bold, title),

  xLarge: build(xLarge, regular, body),
  xLargeMed: build(xLarge, med, body),
  xLargeBold: build(xLarge, bold, body),
  xLargeTitle: build(xLarge, regular, title),
  xLargeTitleMed: build(xLarge, med, title),
  xLargeTitleBold: build(xLarge, bold, title),

  xxLarge: build(xxLarge, regular, body),
  xxLargeMed: build(xxLarge, med, body),
  xxLargeBold: build(xxLarge, bold, body),
  xxLargeTitle: build(xxLarge, regular, title),
  xxLargeTitleMed: build(xxLarge, med, title),
  xxLargeTitleBold: build(xxLarge, bold, title),

  xxxLarge: build(xxxLarge, regular, body),
  xxxLargeMed: build(xxxLarge, med, body),
  xxxLargeBold: build(xxxLarge, bold, body),
  xxxLargeTitle: build(xxxLarge, regular, title),
  xxxLargeTitleMed: build(xxxLarge, med, title),
  xxxLargeTitleBold: build(xxxLarge, bold, title),
} as const;

export type KaiTypographyName = keyof typeof kaiTypography;
